'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Search, SlidersHorizontal, Map, List, LayoutTemplate, ChevronDown, X, Sparkles, MapPin, Loader2 } from 'lucide-react';
import type { SearchFilters, ViewMode } from './PropertySearchClient';
import { searchLocations } from '@/lib/locations';

interface SearchTopBarProps {
  filters: SearchFilters;
  updateFilter: <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => void;
  onOpenFilters: () => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

const SORT_OPTIONS = [
  { value: 'onerilen', label: 'Önerilen' },
  { value: 'en-yeni', label: 'En Yeni' },
  { value: 'fiyat-asc', label: 'Fiyat: Düşükten Yükseğe' },
  { value: 'fiyat-desc', label: 'Fiyat: Yüksekten Düşüğe' },
  { value: 'alan-asc', label: 'm²: Küçükten Büyüğe' },
  { value: 'alan-desc', label: 'm²: Büyükten Küçüğe' },
  { value: 'fiyat-dustu', label: 'Fiyatı Düşenler' },
  { value: 'yatirim', label: 'Yatırım Fırsatları' },
  { value: 'en-cok-goruntulenen', label: 'En Çok Görüntülenenler' },
];

const LISTING_TYPES = [
  { value: 'satilik', label: 'Satılık' },
  { value: 'kiralik', label: 'Kiralık' },
  { value: 'all', label: 'Tümü' },
];

interface LocationSuggestion {
  label: string;
  sublabel: string;
  type: 'province' | 'district' | 'neighborhood';
  province: string;
  district: string;
  neighborhood: string;
}

export default function SearchTopBar({ filters, updateFilter, onOpenFilters, viewMode, setViewMode }: SearchTopBarProps) {
  const [searchText, setSearchText] = useState(() => {
    const parts = [filters.city, filters.district].filter(Boolean);
    return parts.join(', ');
  });
  const [showSort, setShowSort] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentSort = SORT_OPTIONS.find(o => o.value === filters.sortBy) || SORT_OPTIONS[0];

  const activeFilterCount = [
    filters.propertyType.length > 0,
    filters.priceMin || filters.priceMax,
    filters.areaMin || filters.areaMax,
    filters.bedrooms.length > 0,
    filters.features.length > 0,
    filters.buildingAge,
    filters.heating.length > 0,
  ].filter(Boolean).length;

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchLocations(value);
      const items: LocationSuggestion[] = [
        ...results.provinces.map((p) => ({
          label: p.name,
          sublabel: 'İl',
          type: 'province' as const,
          province: p.name,
          district: '',
          neighborhood: '',
        })),
        ...results.districts.map((d) => ({
          label: d.name,
          sublabel: d.province_name,
          type: 'district' as const,
          province: d.province_name,
          district: d.name,
          neighborhood: '',
        })),
        ...results.neighborhoods.map((n) => ({
          label: n.name,
          sublabel: `${n.district_name}, ${n.province_name}`,
          type: 'neighborhood' as const,
          province: n.province_name,
          district: n.district_name,
          neighborhood: n.name,
        })),
      ];
      setSuggestions(items);
      setSearching(false);
    }, 300);
  };

  const handleSuggestionSelect = (s: LocationSuggestion) => {
    updateFilter('city', s.province);
    updateFilter('district', s.district);
    const displayParts = [s.province, s.district, s.neighborhood].filter(Boolean);
    setSearchText(displayParts.join(', '));
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const clearLocation = () => {
    setSearchText('');
    updateFilter('city', '');
    updateFilter('district', '');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="bg-white border-b border-border px-4 py-3 flex-shrink-0">
      <div className="flex items-center gap-3 flex-wrap lg:flex-nowrap">
        {/* Listing Type Tabs */}
        <div className="flex items-center bg-muted rounded-xl p-1 flex-shrink-0">
          {LISTING_TYPES.map((type) => (
            <button
              key={`lt-${type.value}`}
              onClick={() => updateFilter('listingType', type.value as SearchFilters['listingType'])}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-150 ${filters.listingType === type.value ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Location Search Input with real DB suggestions */}
        <div ref={searchRef} className="flex-1 min-w-48 relative">
          <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary z-10" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => { if (searchText.trim()) setShowSuggestions(true); }}
            placeholder="Şehir, ilçe, mahalle..."
            className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-border text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
          />
          {searching && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary animate-spin" />
          )}
          {!searching && searchText && (
            <button onClick={clearLocation} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          )}

          {/* Location Suggestions Dropdown */}
          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-fade-in">
              {suggestions.length === 0 && !searching ? (
                <p className="text-xs text-muted-foreground text-center py-3 px-4">Sonuç bulunamadı</p>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {suggestions.map((s, i) => (
                    <button
                      key={`sug-${i}`}
                      onMouseDown={() => handleSuggestionSelect(s)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-primary-light hover:text-primary text-sm text-left transition-colors"
                    >
                      <MapPin size={14} className="text-primary flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-foreground">{s.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">{s.sublabel}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Search Button */}
        <button className="hidden md:flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-primary/40 text-primary text-sm font-semibold hover:bg-primary-light transition-colors flex-shrink-0">
          <Sparkles size={15} />
          AI Ara
        </button>

        {/* Filters Button */}
        <button
          onClick={onOpenFilters}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all flex-shrink-0 ${activeFilterCount > 0 ? 'border-primary bg-primary-light text-primary' : 'border-border text-foreground hover:bg-muted'}`}
        >
          <SlidersHorizontal size={15} />
          <span className="hidden sm:inline">Filtreler</span>
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Sort */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowSort(!showSort)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            <span className="hidden sm:inline">{currentSort.label}</span>
            <span className="sm:hidden">Sırala</span>
            <ChevronDown size={14} className={`transition-transform ${showSort ? 'rotate-180' : ''}`} />
          </button>
          {showSort && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-fade-in">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={`sort-${option.value}`}
                  onClick={() => { updateFilter('sortBy', option.value); setShowSort(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${filters.sortBy === option.value ? 'bg-primary-light text-primary font-semibold' : 'text-foreground hover:bg-muted font-medium'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* View Mode Toggle — desktop */}
        <div className="hidden lg:flex items-center bg-muted rounded-xl p-1 flex-shrink-0">
          {[
            { mode: 'list' as ViewMode, icon: <List size={16} />, label: 'Liste' },
            { mode: 'split' as ViewMode, icon: <LayoutTemplate size={16} />, label: 'Bölünmüş' },
            { mode: 'map' as ViewMode, icon: <Map size={16} />, label: 'Harita' },
          ].map(({ mode, icon, label }) => (
            <button
              key={`view-${mode}`}
              onClick={() => setViewMode(mode)}
              title={label}
              className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${viewMode === mode ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* Active filter chips */}
      {(filters.city || filters.district || filters.bedrooms.length > 0 || filters.propertyType.length > 0 || filters.priceMin || filters.priceMax) && (
        <div className="flex items-center gap-2 mt-2 overflow-x-auto scrollbar-hide">
          {filters.city && (
            <span
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary-light text-primary text-xs font-semibold flex-shrink-0 cursor-pointer hover:bg-primary hover:text-white transition-colors"
              onClick={() => { updateFilter('city', ''); updateFilter('district', ''); setSearchText(''); }}
            >
              <MapPin size={10} />
              {filters.district ? `${filters.city} / ${filters.district}` : filters.city}
              <X size={10} />
            </span>
          )}
          {filters.bedrooms.map((b) => (
            <span
              key={`chip-bed-${b}`}
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary-light text-primary text-xs font-semibold flex-shrink-0 cursor-pointer hover:bg-primary hover:text-white transition-colors"
              onClick={() => updateFilter('bedrooms', filters.bedrooms.filter(x => x !== b))}
            >
              {b}+1 <X size={10} />
            </span>
          ))}
          {filters.propertyType.map((pt) => (
            <span
              key={`chip-pt-${pt}`}
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary-light text-primary text-xs font-semibold flex-shrink-0 cursor-pointer hover:bg-primary hover:text-white transition-colors"
              onClick={() => updateFilter('propertyType', filters.propertyType.filter(x => x !== pt))}
            >
              {pt} <X size={10} />
            </span>
          ))}
          {(filters.priceMin || filters.priceMax) && (
            <span
              className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary-light text-primary text-xs font-semibold flex-shrink-0 cursor-pointer hover:bg-primary hover:text-white transition-colors"
              onClick={() => { updateFilter('priceMin', ''); updateFilter('priceMax', ''); }}
            >
              {filters.priceMin && `₺${filters.priceMin}`}
              {filters.priceMin && filters.priceMax && ' – '}
              {filters.priceMax && `₺${filters.priceMax}`}
              <X size={10} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}